import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import anime from "animejs";

interface CompletionCelebrationProps {
  totalComplete: number;
  totalFailed: number;
  elapsed: string;
  onRetryAllFailed: () => void;
  onUploadMore: () => void;
}

export function CompletionCelebration({
  totalComplete,
  totalFailed,
  elapsed,
  onRetryAllFailed,
  onUploadMore,
}: CompletionCelebrationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<SVGSVGElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const animatedRef = useRef(false);

  useEffect(() => {
    if (animatedRef.current) return;
    animatedRef.current = true;

    // Particle burst
    const svg = particlesRef.current;
    if (svg) {
      const particles = svg.querySelectorAll("circle");
      anime({
        targets: particles,
        translateX: () => anime.random(-60, 60),
        translateY: () => anime.random(-60, 60),
        scale: [1, 0],
        opacity: [1, 0],
        easing: "easeOutCubic",
        duration: 800,
        delay: anime.stagger(30),
      });
    }

    // Summary entrance
    const container = containerRef.current;
    if (container) {
      anime({
        targets: container,
        opacity: [0, 1],
        translateY: [20, 0],
        easing: "easeOutCubic",
        duration: 500,
        delay: 200,
      });
    }

    // CTA buttons stagger
    const cta = ctaRef.current;
    if (cta) {
      const buttons = cta.querySelectorAll("button");
      anime({
        targets: buttons,
        opacity: [0, 1],
        translateY: [16, 0],
        easing: "easeOutCubic",
        duration: 400,
        delay: anime.stagger(80, { start: 500 }),
      });
    }
  }, []);

  const particleCount = 12;

  return (
    <div className="flex flex-col items-center py-8">
      {/* Particle burst SVG */}
      <svg
        ref={particlesRef}
        width="100"
        height="100"
        viewBox="-50 -50 100 100"
        className="mb-4"
      >
        {Array.from({ length: particleCount }).map((_, i) => {
          const angle = (i / particleCount) * Math.PI * 2;
          const x = Math.cos(angle) * 4;
          const y = Math.sin(angle) * 4;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={2.5}
              fill={totalFailed === 0 ? "var(--tr-accent)" : "var(--tr-text-2)"}
              style={{ willChange: "transform, opacity" }}
            />
          );
        })}
      </svg>

      {/* Summary */}
      <div
        ref={containerRef}
        className="text-center"
        style={{ opacity: 0 }}
      >
        <p className="text-lg font-semibold text-ink">
          {totalComplete} rider{totalComplete !== 1 ? "s" : ""} extracted
        </p>
        {totalFailed > 0 && (
          <p className="mt-1 text-sm text-bad">
            {totalFailed} failed
          </p>
        )}
        <p className="mt-1 font-mono text-xs text-ink-3">
          Completed in {elapsed}
        </p>
      </div>

      {/* Next steps guidance */}
      <div
        className="card mt-6 w-full max-w-md p-4"
        style={{ opacity: 0 }}
        ref={(el) => {
          if (!el) return;
          setTimeout(() => {
            anime({
              targets: el,
              opacity: [0, 1],
              translateY: [12, 0],
              easing: "easeOutCubic",
              duration: 400,
              delay: 400,
            });
          }, 0);
        }}
      >
        <p className="text-sm font-medium text-ink-2">Next steps</p>
        <p className="mt-1 text-xs text-ink-3">
          {totalComplete} rider{totalComplete !== 1 ? "s" : ""} extracted. Review and approve before assigning to a festival.
        </p>
      </div>

      {/* CTA buttons */}
      <div ref={ctaRef} className="mt-4 flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={() => navigate("/riders?status=extracted")}
          className="btn btn--accent"
          style={{ opacity: 0 }}
        >
          Review riders
        </button>
        <button
          onClick={() => navigate("/festivals")}
          className="btn btn--ghost"
          style={{ opacity: 0 }}
        >
          Go to festivals
        </button>
        {totalFailed > 0 && (
          <button
            onClick={onRetryAllFailed}
            className="btn btn--bad"
            style={{ opacity: 0 }}
          >
            Retry failed ({totalFailed})
          </button>
        )}
        <button
          onClick={onUploadMore}
          className="btn btn--ghost"
          style={{ opacity: 0 }}
        >
          Upload more
        </button>
      </div>
    </div>
  );
}
